import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { Alert, Badge, Card, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { formatDate, type SeparationDetail, statusName, statusTone } from './data';
import { CancelCard, DecisionCard, DecisionSummary, OriginTasksCard } from './RequestParts';

/** Detalle de una baja: lo que deja la persona (licencias, equipo y trabajo abierto), la decisión y el avance. */
export function SeparationDetailPage() {
  const { number = '' } = useParams();
  const location = useLocation();
  const sep = useQuery({ queryKey: ['usuarios', 'bajas', 'detalle', number], queryFn: () => api<SeparationDetail>(`/api/usuarios/bajas/${number}`) });
  const [notice, setNotice] = useState<string | null>(null);

  if (!sep.data) return sep.error ? <Alert>{errorMessage(sep.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  const r = sep.data;
  const created = (location.state as { created?: boolean } | null)?.created;
  const pending = [
    { n: r.pending.reports, key: 'us.deja_subordinados' },
    { n: r.pending.tickets, key: 'us.deja_tickets' },
    { n: r.pending.tasks, key: 'us.deja_tareas' },
    { n: r.pending.projects, key: 'us.deja_proyectos' },
  ] as const;
  const leavesWork = pending.some((p) => p.n > 0);

  return (
    <div className="space-y-4">
      <div>
        <Link to="/usuarios/bajas" className="text-sm text-marca hover:underline">
          ← {t('us.bajas_titulo')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-tinta">
          <span className="mr-2 font-mono text-base text-slate-500">{r.code}</span>
          {r.fullName}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
          <Badge tone={statusTone(r.status)}>{statusName(r.status)}</Badge>
          {r.appliedAt && <span className="text-slate-600">· {t('us.baja_aplicada_el', { fecha: formatDate(r.appliedAt) })}</span>}
          <span className="text-slate-500">· {r.organization}</span>
        </div>
      </div>
      {created && <Alert tone="success">{t('us.baja_creada', { codigo: r.code })}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}
      <DecisionSummary status={r.status} decidedBy={r.decidedBy} decidedAt={r.decidedAt} reason={r.decisionReason} />

      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-4">
          <Card title={t('us.datos_persona')}>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
              <dt className="text-slate-500">{t('us.campo_correo')}</dt>
              <dd>{r.email ?? '—'}</dd>
              <dt className="text-slate-500">{t('us.campo_departamento')}</dt>
              <dd>{r.department ?? '—'}</dd>
              <dt className="text-slate-500">{t('us.campo_cargo')}</dt>
              <dd>{r.position ?? '—'}</dd>
              <dt className="text-slate-500">{t('us.campo_jefe')}</dt>
              <dd>{r.manager ?? '—'}</dd>
              <dt className="text-slate-500">{t('us.campo_ultimo_dia')}</dt>
              <dd>{formatDate(r.lastDay)}</dd>
              <dt className="text-slate-500">{t('us.col_solicitante')}</dt>
              <dd>
                {r.requester ?? '—'} · {formatDate(r.createdAt)}
              </dd>
            </dl>
            {r.reason && <p className="mt-3 whitespace-pre-wrap border-t border-slate-100 pt-3 text-sm text-slate-700">{r.reason}</p>}
          </Card>
          <Card title={t('us.que_deja')}>
            <div className="space-y-3 text-sm">
              <div>
                <p className="font-medium text-slate-700">{t('us.campo_licencias')}</p>
                {r.licenses.length === 0 ? (
                  <p className="text-slate-500">{t('us.ninguna')}</p>
                ) : (
                  <ul className="list-disc pl-5 text-slate-800">
                    {r.licenses.map((l) => (
                      <li key={l.id}>
                        {l.name} <span className="text-xs text-slate-500">· {l.billing === 'mensual' ? t('us.deja_mensual') : t('us.deja_anual')}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p className="font-medium text-slate-700">{t('us.trabajo_abierto')}</p>
                {leavesWork ? (
                  <ul className="list-disc pl-5 text-slate-800">
                    {pending
                      .filter((p) => p.n > 0)
                      .map((p) => (
                        <li key={p.key}>{t(p.key, { n: p.n })}</li>
                      ))}
                  </ul>
                ) : (
                  <p className="text-slate-500">{t('us.sin_trabajo_abierto')}</p>
                )}
              </div>
            </div>
          </Card>
          <OriginTasksCard tasks={r.tasks} empty={r.status === 'aprobada' ? t('us.sin_tareas') : t('us.tareas_al_aprobar_baja')} />
        </div>
        <div className="space-y-4">
          {r.can.decide && (
            <DecisionCard
              path={`/api/usuarios/bajas/${r.number}`}
              help={t('us.decidir_baja_ayuda')}
              warning={leavesWork ? <Alert tone="warning">{t('us.advertencia_trabajo')}</Alert> : undefined}
              onDone={(approved) => setNotice(approved ? t('us.baja_aprobada_ok', { fecha: formatDate(r.lastDay) }) : t('us.rechazada_ok'))}
            />
          )}
          {r.can.cancel && <CancelCard path={`/api/usuarios/bajas/${r.number}`} />}
        </div>
      </div>
    </div>
  );
}
